import React, { useState } from "react";
import { X, Wallet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import useRequest from "../../../components/hook/use-req";
import { formatCurrency } from "../../../utils/function";

interface FundWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  orgName: string;
  currentBalance: number;
  currency: string;
  // Called after a successful fund — the caller re-fetches the org detail
  // (balance, total inflow, transactions) rather than this modal trying to
  // guess the new numbers itself.
  onFunded: () => void;
}

// "Manual Wallet Funding" — POST /admin/organizations/{id}/fund. Styled to
// this app's own blue/indigo palette (matching the Global Admin dashboard
// and sidebar on this branch — not copied verbatim from the reference
// design's colors) so it fits the rest of the Global Admin Businesses
// feature.
const FundWalletModal: React.FC<FundWalletModalProps> = ({
  isOpen,
  onClose,
  orgId,
  orgName,
  currentBalance,
  currency,
  onFunded,
}) => {
  const token = localStorage.getItem("authToken");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { makeRequest: fundWallet } = useRequest(`/admin/organizations/${orgId}/fund`, "POST", {
    Authorization: `Bearer ${token}`,
  });

  const reset = () => {
    setAmount("");
    setRemarks("");
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    const parsed = Number(amount);
    if (!amount || Number.isNaN(parsed) || parsed <= 0) {
      toast.error("Enter a valid funding amount.");
      return;
    }
    setSubmitting(true);
    const [response, status] = await fundWallet({ amount: parsed, remarks: remarks.trim() || undefined });
    setSubmitting(false);
    if (status >= 200 && status < 300 && response?.status !== false) {
      toast.success(response?.message || "Wallet funded successfully.");
      reset();
      onFunded();
      onClose();
    } else {
      toast.error(response?.detail || response?.message || "Failed to fund wallet.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
              <Wallet size={18} className="text-blue-600 dark:text-blue-400" />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">Manual Wallet Funding</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Update business wallet balance</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-[#7B7B7B] font-semibold hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          {/* Target business / current balance */}
          <div className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3.5">
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide">Target Business</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{orgName}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[11px] font-medium text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide">Current Balance</p>
              <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                {formatCurrency(currentBalance, currency, "symbol")}
              </p>
            </div>
          </div>

          {/* Funding amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Funding Amount</label>
            <input
              type="number"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1.5">The amount will be added instantly to the current balance.</p>
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Transaction Remarks (Optional)</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              placeholder="Provide a reason for this manual adjustment…"
              className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20">
            <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
              <span className="font-semibold">Important:</span> Please double-check the funding amount before proceeding.
              This action will create an immutable transaction record and cannot be undone.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 px-5 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !amount}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors"
          >
            <CheckCircle2 size={15} />
            {submitting ? "Funding…" : "Confirm Funding"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FundWalletModal;
